import { readForceSoloTarif } from './soloMode';
import { defaultSettings } from '../store/slices/settingsSlice';

export const getYearFromMandantNr = (nr: string): number => {
    if (!nr || typeof nr !== 'string') return 0;
    try {
        // Neu + Alt: explizites NN/YYYY bevorzugt (auch innerhalb `MA - 02/2026 - …`)
        const slashYear = nr.match(/(\d{2})\/(20\d{2})/);
        if (slashYear) {
            const y = parseInt(slashYear[2], 10);
            if (!isNaN(y)) return y;
        }
        const slashParts = nr.split('/');
        if (slashParts.length > 1) {
            const parsed = parseInt(slashParts[slashParts.length - 1], 10);
            if (!isNaN(parsed) && parsed >= 2000 && parsed < 2100) return parsed;
        }
        const match = nr.match(/(20\d{2})/);
        return match ? parseInt(match[1], 10) : 0;
    } catch {
        return 0;
    }
};

export type TarifTier = 'solo' | 'teamBase' | 'teamPro' | 'teamProPlus';

/**
 * TeamPro / TeamPro+-Schwelle — immer live wie in Globaldaten „Schwelle“:
 * Insider_Team / (Grobkundenaufschlag / 100).
 * Gespeichertes Grobkundenaufschlag_ab nur als Fallback (kann veralten).
 */
export const resolveTeamProThreshold = (
    global?: Record<string, number | undefined> | null
): number | undefined => {
    if (!global) return undefined;

    const basePrice = Number(global.Insider_Team ?? 0);
    let percentage = Number(global.Grobkundenaufschlag ?? 0);
    // Legacy: echte Brüche >1% würden als 40 statt 0.4 gespeichert — hier nur 0–100 als %-Punkte
    if (basePrice > 0 && percentage > 0) {
        return Math.round(basePrice / (percentage / 100));
    }

    const stored = Number(global.Grobkundenaufschlag_ab ?? 0);
    if (stored > 0) return stored;
    return undefined;
};

export interface ClientPriceInput {
    settings?: { global?: Record<string, number> } | null;
    /** @deprecated Preis folgt Flags; Feld bleibt für Call-Sites erhalten */
    lizenzTyp?: string;
    client?: any;
    forceSoloTarif?: boolean;
    forceNewCustomerPremium?: boolean;
    disableNewCustomerPremium?: boolean;
    /**
     * true = Admin hat das Neukunden-Häkchen bewusst gegen die Nummer gestellt.
     * Ohne dieses Flag gilt immer das Jahr aus der Mandanten-Nr. (alte Force/Disable werden ignoriert).
     */
    neukundenManualOverride?: boolean;
    mandantNr?: string;
    isProBono?: boolean;
    /** Netto-Monatsumsatz für TeamPro+ (ohne Wert → Flat TeamPro) */
    nettoMonatsumsatz?: number | null;
    /** Explizite Overrides (sonst aus client / clientData) */
    isSolo?: boolean;
    wantsEvaluation?: boolean;
    hasEmployeeEval?: boolean;
    isNewCustomer?: boolean;
    /**
     * Wenn gesetzt: Preis für genau diesen Tarif (z. B. aus Header-Badge),
     * ohne erneute Flag-/Nummer-Inferenz.
     */
    tier?: TarifTier;
}

const readClientField = (client: any, key: string): any => {
    if (!client) return undefined;
    if (client[key] !== undefined && client[key] !== null) return client[key];
    if (client.clientData?.[key] !== undefined && client.clientData?.[key] !== null) {
        return client.clientData[key];
    }
    return undefined;
};

/** isSolo — Admin forceSoloTarif === true schlägt alles. false blockiert isSolo nicht. */
export const resolvePriceIsSolo = (input: ClientPriceInput): boolean => {
    const client = input.client;
    if (input.forceSoloTarif === true) return true;
    if (readForceSoloTarif(client || {})) return true;
    if (input.isSolo !== undefined) return input.isSolo === true;
    if (readClientField(client, 'isSolo') === true) return true;
    if (readClientField(client, 'isSoloBusiness') === true) return true;
    return false;
};

/** wantsEvaluation ≡ hasEmployeeEval (Auth-Flag). */
export const resolvePriceWantsEvaluation = (input: ClientPriceInput): boolean => {
    if (input.wantsEvaluation !== undefined) return input.wantsEvaluation === true;
    if (input.hasEmployeeEval !== undefined) return input.hasEmployeeEval === true;
    return readClientField(input.client, 'hasEmployeeEval') === true;
};

/**
 * isNewCustomer — Priorität:
 * 1) Explizite Live-Angabe am Input (Admin-Vorschau)
 * 2) Manual-Override force/disable
 * 3) Registrierungs-Flags isExistingCustomer / isBestandskunde
 * 4) Jahr aus Mandanten-Nr. (>= 2027 = Neukunde) für Altdaten ohne Flags
 */
export const resolvePriceIsNewCustomer = (input: ClientPriceInput): boolean => {
    const client = input.client;

    const flagTrue = (v: unknown): boolean =>
        v === true || v === 1 || v === '1' || v === 'true';

    // Explizite Live-Angabe (z. B. Admin-Vorschau) hat Vorrang
    if (input.isNewCustomer !== undefined) return input.isNewCustomer === true;

    const manual =
        flagTrue(input.neukundenManualOverride) ||
        flagTrue(readClientField(client, 'neukundenManualOverride'));

    if (manual) {
        const force =
            flagTrue(input.forceNewCustomerPremium) ||
            flagTrue(readClientField(client, 'forceNewCustomerPremium'));
        const disable =
            flagTrue(input.disableNewCustomerPremium) ||
            flagTrue(readClientField(client, 'disableNewCustomerPremium'));
        if (force && !disable) return true;
        if (disable && !force) return false;
    }

    // Registrierungs-Flags (Scheibe 1): striktes true/false, undefined = Altdaten
    const isExistingCustomer = readClientField(client, 'isExistingCustomer');
    const isBestandskunde = readClientField(client, 'isBestandskunde');
    if (isExistingCustomer === false || isBestandskunde === false) {
        return true;
    }
    if (isExistingCustomer === true || isBestandskunde === true) {
        return false;
    }

    // Noch keine echte Kd.-Nr. = frischer Neukunde (auch wenn Flags fehlen)
    const customerNumber = String(
        readClientField(client, 'customerNumber') || client?.customerNumber || ''
    ).trim();
    if (customerNumber === 'PENDING') {
        return true;
    }

    const mandantNr =
        input.mandantNr ||
        client?.mandantNr ||
        client?.clientData?.mandantNr ||
        client?.customerNumber ||
        '';
    return isNeukundenYearFromMandantNr(mandantNr);
};

/** true, wenn die Basisnummer / Mandanten-Nr. ein Neukunden-Jahr (>= 2027) trägt */
export const isNeukundenYearFromMandantNr = (nr: string | null | undefined): boolean => {
    const y = getYearFromMandantNr(String(nr || ''));
    return !isNaN(y) && y >= 2027;
};

export const resolveNettoMonatsumsatz = (input: ClientPriceInput): number | null => {
    const client = input.client;
    const hasBranches =
        client?.hasBranches === true || client?.clientData?.hasBranches === true;

    // Explizit übergeben: 0 bei Filiale zählt; bei Einzelbetrieb 0 → BWA versuchen
    if (input.nettoMonatsumsatz !== undefined && input.nettoMonatsumsatz !== null) {
        const n = Number(input.nettoMonatsumsatz);
        if (Number.isFinite(n) && (n > 0 || hasBranches)) return n;
    }

    if (hasBranches) {
        const aggCandidates = [
            readClientField(client, 'aggregatedNettoMonatsumsatz'),
            readClientField(client, 'nettoMonatsumsatz'),
        ];
        for (const c of aggCandidates) {
            if (c === undefined || c === null) continue;
            const n = Number(c);
            if (Number.isFinite(n)) return n;
        }
    }
    const candidates = [
        readClientField(client, 'nettoMonatsumsatz'),
        readClientField(client, 'aggregatedNettoMonatsumsatz'),
        client?.bwaData?.monatsumsatzNetto,
        client?.clientData?.bwaData?.monatsumsatzNetto,
    ];
    for (const c of candidates) {
        if (c === undefined || c === null) continue;
        const n = Number(c);
        // 0 nicht als „gültiger Umsatz“ behandeln → BWA-Fallback
        if (Number.isFinite(n) && n > 0) return n;
    }
    // Live aus BWA-Jahresumsatz / Zeitraum (wie calculations.ts)
    const bwa = client?.bwaData || client?.clientData?.bwaData;
    if (bwa) {
        const zeitraumRaw = Number(bwa.zeitraum);
        const zeitraum = Number.isFinite(zeitraumRaw) && zeitraumRaw > 0 ? zeitraumRaw : 12;
        const parseEuro = (val: unknown): number => {
            if (val === null || val === undefined) return 0;
            if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
            if (typeof val === 'string') {
                const clean = val.replace(/\./g, '').replace(',', '.');
                const num = parseFloat(clean);
                return Number.isFinite(num) ? num : 0;
            }
            return 0;
        };
        const jahres =
            parseEuro(bwa.umsatz) +
            (Array.isArray(bwa.prognoseZeilen) ? bwa.prognoseZeilen : [])
                .filter((z: any) => z && z.aktiv)
                .reduce((sum: number, z: any) => sum + parseEuro(z.jahresumsatz), 0);
        if (jahres > 0) return jahres / zeitraum;
    }
    return null;
};

/** Manuelle Tarif-Matrix (+ einzige Automatik TeamPro+). */
export const resolveTarifTier = (input: ClientPriceInput): TarifTier => {
    const isSolo = resolvePriceIsSolo(input);
    if (isSolo) return 'solo';

    const client = input.client;
    const hasBranches =
        client?.hasBranches === true || client?.clientData?.hasBranches === true;

    const gEarly = input.settings?.global;
    const thresholdEarly = resolveTeamProThreshold(gEarly) ?? 0;
    const umsatzEarly = resolveNettoMonatsumsatz(input);

    // Filialbetrieb: Live-Umsatz schlägt sticky Plus-Flag (Downgrade wenn unter Schwelle)
    if (hasBranches) {
        if (umsatzEarly !== null && thresholdEarly > 0) {
            if (umsatzEarly >= thresholdEarly) return 'teamProPlus';
            // unter Schwelle → weiter zu TeamPro/TeamBase (kein sticky Plus)
        } else if (client?.isTeamProPlus === true || client?.clientData?.isTeamProPlus === true) {
            return 'teamProPlus';
        }
    }

    const wantsEvaluation = resolvePriceWantsEvaluation(input);
    if (!wantsEvaluation) return 'teamBase';

    const g = input.settings?.global;
    const threshold = resolveTeamProThreshold(g) ?? 0;
    const umsatz = umsatzEarly ?? resolveNettoMonatsumsatz(input);
    if (umsatz !== null && threshold > 0 && umsatz >= threshold) {
        return 'teamProPlus';
    }
    return 'teamPro';
};

/**
 * Monatspreis aus manueller Flag-Matrix.
 * Solo / TeamBase / TeamPro (+ Neukunden-Aufschlag).
 * TeamPro+: max(Umsatz × Grobkundenaufschlag%, Insider_Team), danach derselbe Neukunden-Aufschlag.
 * (Plus darf nie unter dem TeamPro-Flat liegen — sonst „günstiger trotz Mehrumsatz“.)
 */
export const calculateClientMonthlyFee = (input: ClientPriceInput): number => {
    const { settings, isProBono } = input;
    if (isProBono) return 0;
    if (!settings?.global) return 0;

    const g = settings.global;
    const tier = input.tier ?? resolveTarifTier(input);

    let basePrice = 0;

    if (tier === 'teamProPlus') {
        const umsatz = resolveNettoMonatsumsatz(input);
        const ratePct = Number(g.Grobkundenaufschlag ?? 0);
        const teamProFlat = Number(g.Insider_Team ?? 0);
        if (umsatz === null || ratePct <= 0) {
            basePrice = teamProFlat;
        } else {
            const umsatzPreis = umsatz * (ratePct / 100);
            // Floor = TeamPro-Flat: Schwellen-/Formel-Mismatch darf Plus nicht verbilligen
            basePrice = Math.max(umsatzPreis, teamProFlat);
        }
    } else if (tier === 'solo') {
        basePrice = Number(g.Insider_Solo ?? 0);
    } else if (tier === 'teamBase') {
        basePrice = Number(g.basicStandardPreis ?? 0);
    } else {
        basePrice = Number(g.Insider_Team ?? 0);
    }

    if (basePrice === 0) return 0;

    if (resolvePriceIsNewCustomer(input)) {
        const rawAufschlag = Number(g.Neukunden_Aufschlag);
        // Firebase/UI speichern Prozentpunkte (30 = 30 %). Fallback auf Default.
        let aufschlagPct =
            Number.isFinite(rawAufschlag) && rawAufschlag > 0
                ? rawAufschlag
                : Number(defaultSettings.global.Neukunden_Aufschlag ?? 0);
        // Schutz: falls versehentlich als Bruch (0.3) gespeichert → in Prozentpunkte
        if (aufschlagPct > 0 && aufschlagPct < 1) {
            aufschlagPct = aufschlagPct * 100;
        }
        if (aufschlagPct > 0) {
            basePrice = basePrice * (1 + aufschlagPct / 100);
        }
    }

    return Math.round(basePrice * 100) / 100;
};

export const formatMonthlyFeeDe = (value: number): string => {
    if (!value) return '';
    return value.toFixed(2).replace('.', ',');
};
